import { JobServicePromiseClient } from '@taxfyle/api-internal/internal/job_grpc_web_pb'
import { JobNotesServicePromiseClient } from '@taxfyle/api-internal/internal/job_note_grpc_web_pb'
import { JobAdminServicePromiseClient } from '@taxfyle/api-internal/internal/job_admin_grpc_web_pb'
import { DraftAdminServicePromiseClient } from '@taxfyle/api-internal/internal/draft_admin_grpc_web_pb'
import { TicketServicePromiseClient } from '@taxfyle/api-internal/internal/ticket_grpc_web_pb'
import { ConsultationServicePromiseClient } from '@taxfyle/api-internal/internal/consultation_grpc_web_pb'
import { BookkeepingServicePromiseClient } from '@taxfyle/api-internal/internal/bookkeeping_grpc_web_pb'
import { DocumentRequestServicePromiseClient } from '@taxfyle/api-internal/internal/document_request_grpc_web_pb'
import { TxfJobServicePromiseClient } from '@taxfyle/api-internal/internal/txf_grpc_web_pb'
import { DraftServicePromiseClient } from '@taxfyle/api-internal/internal/draft_grpc_web_pb'
import { JobScopeChangeServicePromiseClient } from '@taxfyle/api-internal/internal/job_scope_change_grpc_web_pb'
import { InsightsServicePromiseClient } from '@taxfyle/api-internal/internal/insights_grpc_web_pb'
import { InfoGatheringServicePromiseClient } from '@taxfyle/api-internal/internal/info_gathering_grpc_web_pb'
import { JobEventsServicePromiseClient } from '@taxfyle/api-internal/internal/job_event_grpc_web_pb'
import { CallCenterServicePromiseClient } from '@taxfyle/api-internal/internal/call_center_grpc_web_pb'
import { BusinessProfileServicePromiseClient } from '@taxfyle/api-internal/internal/business_profile_grpc_web_pb'
import { IndividualProfileServicePromiseClient } from '@taxfyle/api-internal/internal/individual_profile_grpc_web_pb'
import { TaxRefundMethodServicePromiseClient } from '@taxfyle/api-internal/internal/tax_refund_methods_grpc_web_pb'
import { JobStatsServicePromiseClient } from '@taxfyle/api-internal/internal/job_stats_grpc_web_pb'
import { JobRatingServicePromiseClient } from '@taxfyle/api-internal/internal/job_rating_grpc_web_pb'
import { JobProgressionServicePromiseClient } from '@taxfyle/api-internal/internal/job_progression_grpc_web_pb'
import { RealtimeServicePromiseClient } from '@taxfyle/api-internal/internal/realtime_grpc_web_pb'
import {
  RealtimeEvent,
  StreamEventOptions,
} from '@taxfyle/api-internal/internal/realtime_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { merge, type Observable, of } from 'rxjs'
import {
  delay,
  exhaustMap,
  filter,
  map,
  retry,
  share,
  skip,
  startWith,
  switchMap,
} from 'rxjs/operators'
import { ApiClient, type ApiClientOptions } from '@/infra/api-client'
import {
  enableDevTools,
  fromStreamingCall,
  patchGrpcClient,
} from '@/utils/grpc-util'
import { toURLSearchParams } from '@/utils/url-util'
import { isOnline$ } from '@/utils/browser-util'

export class JobsApi {
  readonly jobs
  readonly jobsAdmin
  readonly jobEvents
  readonly progressions
  readonly jobNotes
  readonly drafts
  readonly draftsAdmin
  readonly tickets
  readonly scopeChangeRequests
  readonly insights
  readonly infoGathering
  readonly consultations
  readonly bookkeeping
  readonly documentRequests
  readonly txf
  readonly callCenter
  readonly businessProfiles
  readonly individualProfiles
  readonly taxRefundMethods
  readonly jobStats
  readonly ratings
  readonly sureprep
  readonly realtime$

  constructor(options: ApiClientOptions) {
    const { baseURL, token$ } = options

    this.jobs = new JobServicePromiseClient(baseURL)
    patchGrpcClient(this.jobs, token$)

    this.jobsAdmin = new JobAdminServicePromiseClient(baseURL)
    patchGrpcClient(this.jobsAdmin, token$)

    this.jobEvents = new JobEventsServicePromiseClient(baseURL)
    patchGrpcClient(this.jobEvents, token$)

    this.progressions = new JobProgressionServicePromiseClient(baseURL)
    patchGrpcClient(this.progressions, token$)

    this.jobNotes = new JobNotesServicePromiseClient(baseURL)
    patchGrpcClient(this.jobNotes, token$)

    this.drafts = new DraftServicePromiseClient(baseURL)
    patchGrpcClient(this.drafts, token$)

    this.draftsAdmin = new DraftAdminServicePromiseClient(baseURL)
    patchGrpcClient(this.draftsAdmin, token$)

    this.tickets = new TicketServicePromiseClient(baseURL)
    patchGrpcClient(this.tickets, token$)

    this.scopeChangeRequests = new JobScopeChangeServicePromiseClient(baseURL)
    patchGrpcClient(this.scopeChangeRequests, token$)

    this.insights = new InsightsServicePromiseClient(baseURL)
    patchGrpcClient(this.insights, token$)

    this.infoGathering = new InfoGatheringServicePromiseClient(baseURL)
    patchGrpcClient(this.infoGathering, token$)

    this.consultations = new ConsultationServicePromiseClient(baseURL)
    patchGrpcClient(this.consultations, token$)

    this.bookkeeping = new BookkeepingServicePromiseClient(baseURL)
    patchGrpcClient(this.bookkeeping, token$)

    this.documentRequests = new DocumentRequestServicePromiseClient(baseURL)
    patchGrpcClient(this.documentRequests, token$)

    this.txf = new TxfJobServicePromiseClient(baseURL)
    patchGrpcClient(this.txf, token$)

    this.callCenter = new CallCenterServicePromiseClient(baseURL)
    patchGrpcClient(this.callCenter, token$)

    this.businessProfiles = new BusinessProfileServicePromiseClient(baseURL)
    patchGrpcClient(this.businessProfiles, token$)

    this.individualProfiles = new IndividualProfileServicePromiseClient(baseURL)
    patchGrpcClient(this.individualProfiles, token$)

    this.taxRefundMethods = new TaxRefundMethodServicePromiseClient(baseURL)
    patchGrpcClient(this.taxRefundMethods, token$)

    this.jobStats = new JobStatsServicePromiseClient(baseURL)
    patchGrpcClient(this.jobStats, token$)

    this.ratings = new JobRatingServicePromiseClient(baseURL)
    patchGrpcClient(this.ratings, token$)

    this.sureprep = new SureprepApi(options)

    const realtime = new RealtimeServicePromiseClient(baseURL ?? '')
    enableDevTools(realtime)
    this.realtime$ = token$.pipe(
      // We don't need to reconnect when the token changes,
      // hence the use of `exhaustMap`. The retry policy at the end
      // will re-subscribe to the source and give us a fresh
      // token if triggered.
      exhaustMap((token) =>
        fromStreamingCall(
          () =>
            realtime.streamEvents(new StreamEventOptions(), {
              Authorization: `Bearer ${token}`,
            }),
          /* errorOnEnd:*/ true,
        ),
      ),
      // Reconnect after 40 seconds of no activity
      realtimeRetryPolicy(40_000),
      share(),
    )
  }
}

/**
 * Realtime stream policy which
 * 1) Reconnects after a short delay if there are any errors.
 * 2) Reconnects if no messages are received within a specific grace period (server
 * is expected to send a ping every so often).
 * @param pingGracePeriodMs Amount of time to wait for a ping before reconnecting
 */
function realtimeRetryPolicy<T>(pingGracePeriodMs: number) {
  return (source$: Observable<T>) =>
    source$.pipe(
      // Emit a ping right away, otherwise we don't start the
      // pipe which emits an error if we don't receive a message.
      startWith(new RealtimeEvent().setPing(new Empty())),
      switchMap((value) =>
        merge(
          of(value),
          // throw an error after timeout if nothing is received
          of(1).pipe(
            delay(pingGracePeriodMs),
            map(() => {
              throw new Error(`No new thing after ${pingGracePeriodMs}ms.`)
            }),
          ),
        ),
      ),
      // Skip the fake ping.
      skip(1),
      retry({
        // Only retry while we're online.
        delay: () => isOnline$.pipe(filter(Boolean), delay(1_000)),
      }),
    )
}

export class SureprepApi extends ApiClient {
  /**
   * Downloads the PBFX binder file for the specified job.
   *
   * @param jobId
   */
  async downloadPBFXFile(jobId: string): Promise<Blob> {
    const request = new Request(
      this._createURL(
        'api/v1.0/sureprepintegration/download-pbfx',
        toURLSearchParams({ jobId }),
      ),
    )
    return this._send(request)
      .then(ApiClient.throwIfNotSuccessful)
      .then((r) => r.blob())
  }
}
